import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploader from '../components/DocumentUploader';

const CreateTender = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    work_category: '',
    location: '',
    estimated_value: '',
    require_emd: false,
    emd_amount: '',
    start_date: '',
    end_date: '',
    scope_of_work: '',
    eligibility_criteria: '',
    required_documents: '',
    site_visit_details: '',
    documents: []
  });
  const [loading, setLoading] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Detect demo accounts → auto-fill sample data so users can experience the full create flow
  const isDemoUser = !!(user?.email || '').toLowerCase().includes('demo');

  useEffect(() => {
    fetchCategories();
    if (isDemoUser) {
      const today = new Date();
      const start = new Date(today); start.setDate(start.getDate() + 3);
      const end = new Date(today); end.setDate(end.getDate() + 17);
      const toInput = (d) => d.toISOString().split('T')[0];
      setFormData({
        title: 'Annual Fire Safety Systems AMC — 102 Flats',
        work_category: 'Fire Safety Systems',
        location: 'Mumbai, Maharashtra',
        estimated_value: '550000',
        require_emd: false,
        emd_amount: '',
        start_date: toInput(start),
        end_date: toInput(end),
        scope_of_work:
          'Comprehensive Annual Maintenance Contract for fire safety systems across 4 buildings:\n' +
          '• Fire alarm panel & smoke detectors (102 units) — monthly inspection\n' +
          '• Fire extinguishers (38 units) — quarterly servicing + refilling as needed\n' +
          '• Sprinkler system — bi-annual pressure test & cleanup\n' +
          '• Fire hydrant pump room — monthly checks, annual statutory certification\n' +
          '• 24×7 emergency response within 2 hours; annual mock drill for residents',
        eligibility_criteria:
          'Minimum 3 years of fire safety AMC experience servicing co-operative societies.\nValid Maharashtra Fire Brigade vendor registration.\nGSTIN, PAN, MSME registration required.',
        required_documents:
          'Company profile · Past 3 years CHS client references · Statutory licenses · GSTIN & PAN · ISO certification (if any) · Insurance / liability cover',
        site_visit_details:
          'Pre-bid site visit on Saturday 11:00 AM. Contact Society Manager at 9876543210 to schedule.',
        documents: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoUser]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e, navigateToLifecycle = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        emd_amount: formData.require_emd && formData.emd_amount ? parseFloat(formData.emd_amount) : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      const { data } = await axios.post(`${API}/tenders`, payload, { withCredentials: true });

      if (navigateToLifecycle) {
        toast.success('Tender saved. Upload Internal Approval documents to proceed.');
        navigate(`/tender/${data.id}`);
      } else {
        toast.success('Tender saved as draft!');
        navigate('/buyer-dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Failed to create tender';

      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }

      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/buyer-dashboard')} className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'hsl(var(--primary))' }}>Create New Tender</h1>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-4xl">
        {isDemoUser && (
          <div className="mb-6 p-4 rounded-lg border-2 border-orange/40 bg-orange/5 flex items-start gap-3" data-testid="demo-autofill-banner">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-orange text-white flex items-center justify-center font-bold text-sm">D</div>
            <div className="flex-1 text-sm">
              <div className="font-bold text-navy">Hands-On Demo — Form Auto-Filled</div>
              <div className="text-foreground/80 text-xs mt-1">
                Realistic sample data has been pre-populated for a Fire Safety Systems AMC. Feel free to edit any field, change the category, or fill in your own details — then click <span className="font-bold">Save &amp; Start Internal Approval</span> to walk through the full 12-stage workflow.
              </div>
            </div>
          </div>
        )}

        <form onSubmit={(e) => handleSubmit(e, 'draft')} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tender Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="e.g., Annual Maintenance Contract for Society"
                  data-testid="tender-title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Work Category *</label>
                  <div className="flex gap-2 items-center">
                    <select
                      value={formData.work_category}
                      onChange={(e) => setFormData({ ...formData, work_category: e.target.value })}
                      required
                      className="flex-1 px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                      data-testid="tender-category-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!formData.work_category) return toast.error('Pick a category first');
                        try {
                          const { data } = await axios.get(`${API}/buyer/tender-template/${encodeURIComponent(formData.work_category)}`, { withCredentials: true });
                          if (!data?.available) return toast.error('No template for this category');
                          setFormData(prev => ({
                            ...prev,
                            scope_of_work: data.scope_of_work || prev.scope_of_work,
                            eligibility_criteria: data.eligibility_criteria || prev.eligibility_criteria,
                            required_documents: data.required_documents || prev.required_documents,
                          }));
                          toast.success(`Loaded v${data.version} template for ${formData.work_category}`);
                        } catch (e) { toast.error('Failed to load template'); }
                      }}
                      className="px-3 py-2 text-xs font-bold rounded bg-navy text-white whitespace-nowrap"
                      data-testid="load-template-btn"
                    >
                      Load Template
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                    placeholder="City, State"
                    data-testid="tender-location-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Estimated Procurement Budget (₹)</label>
                  <input
                    type="number"
                    value={formData.estimated_value}
                    onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                    min="0"
                    placeholder="Optional — leave blank if unknown"
                    className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                    data-testid="tender-value-input"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">Optional. Budget Savings / Variance are calculated only when a value is entered. Negotiation Savings continue in all cases.</div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Require Bid Security / EMD?</label>
                  <div className="flex gap-2" data-testid="tender-require-emd-toggle">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, require_emd: false, emd_amount: '' })}
                      className={`flex-1 px-4 py-3 rounded-md border text-sm font-semibold transition-colors ${!formData.require_emd ? 'bg-navy text-white border-navy' : 'bg-background text-foreground border-input hover:border-navy/40'}`}
                      data-testid="tender-require-emd-no"
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, require_emd: true })}
                      className={`flex-1 px-4 py-3 rounded-md border text-sm font-semibold transition-colors ${formData.require_emd ? 'bg-navy text-white border-navy' : 'bg-background text-foreground border-input hover:border-navy/40'}`}
                      data-testid="tender-require-emd-yes"
                    >
                      Yes
                    </button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Default for housekeeping, AMC and routine society services is No.
                  </p>
                </div>
              </div>

              {formData.require_emd && (
                <div className="grid md:grid-cols-2 gap-4" data-testid="tender-emd-amount-section">
                  <div>
                    <label className="block text-sm font-medium mb-2">EMD Amount (₹) *</label>
                    <input
                      type="number"
                      value={formData.emd_amount}
                      onChange={(e) => setFormData({ ...formData, emd_amount: e.target.value })}
                      required={formData.require_emd}
                      min="0"
                      className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                      data-testid="tender-emd-input"
                      placeholder="e.g. 25000"
                    />
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                    data-testid="tender-start-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                    data-testid="tender-end-date-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Tender Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Scope of Work</label>
                <textarea
                  value={formData.scope_of_work}
                  onChange={(e) => setFormData({ ...formData, scope_of_work: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="Describe the work to be done..."
                  data-testid="tender-scope-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Eligibility Criteria</label>
                <textarea
                  value={formData.eligibility_criteria}
                  onChange={(e) => setFormData({ ...formData, eligibility_criteria: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="e.g., Minimum 5 years experience..."
                  data-testid="tender-eligibility-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Required Documents</label>
                <textarea
                  value={formData.required_documents}
                  onChange={(e) => setFormData({ ...formData, required_documents: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="List required documents..."
                  data-testid="tender-documents-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Attach Tender Documents (Optional)</label>
                <DocumentUploader
                  files={formData.documents}
                  onChange={(documents) => setFormData({ ...formData, documents })}
                  purpose="tender_document"
                  maxFiles={5}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Site Visit / Pre-bid Meeting Details</label>
                <textarea
                  value={formData.site_visit_details}
                  onChange={(e) => setFormData({ ...formData, site_visit_details: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  placeholder="Date, time, and location for site visit..."
                  data-testid="tender-site-visit-input"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading}
              className="btn-outline"
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
              className="btn-primary"
              data-testid="save-and-start-approval-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Save &amp; Start Internal Approval
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTender;
